/**
 * The stale-chunk guard — master spec §3d, Phase 1 step 8.
 *
 * The SPA is bundled INTO the Lambda package, so the previous build's hashed
 * assets cease to exist the instant the function updates. A fresh page load is
 * fine (`index.html` is `no-store`), but a user with the app already open who
 * navigates to a lazily-loaded route after a deploy gets a 404 and a blank
 * screen. Without this the S3-origin trade is worse, not better.
 *
 * [R4] THE RETRY / RELOAD DISTINCTION IS THE WHOLE POINT.
 *
 * Assets are served through the API Gateway throttle (100 rps / 200 burst,
 * §3a), so a throttled asset returns 429 — indistinguishable from a vanished
 * chunk unless you check the status. An unconditional reload on any chunk
 * failure turns a brief throttle into a RELOAD STORM that requests the whole
 * asset set again and throttles harder. Reload only when the chunk is
 * genuinely gone.
 */

export type ChunkFailure = 'gone' | 'transient';

const MAX_ATTEMPTS = 3;
const BASE_DELAY_MS = 400;

/** Vite's message is `Failed to fetch dynamically imported module: <url>`. */
export function chunkUrlFrom(error: unknown): string | null {
  const message = error instanceof Error ? error.message : String(error ?? '');
  const match = /(https?:\/\/\S+?)(?:\s|$|\))/.exec(message);
  return match?.[1] ?? null;
}

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return (
    /dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /ChunkLoadError/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
}

/**
 * Decide whether the chunk is gone or the request merely failed.
 *
 * Preferred: ask for the chunk itself and read the status — a real 404 is the
 * only thing that justifies a reload.
 *
 * Fallback, when the URL cannot be recovered from the error: compare the build
 * id the page loaded with the one the server reports. A DIFFERENT build id
 * means a deploy has replaced the assets, which is exactly "gone"; the same id
 * means the asset should still exist and the failure was transient. This
 * reuses the §3d poll rather than inventing a second signal.
 */
export async function classifyChunkFailure(
  error: unknown,
  deps: {
    fetchImpl?: typeof fetch;
    loadedBuildId?: string;
  } = {},
): Promise<ChunkFailure> {
  const doFetch = deps.fetchImpl ?? fetch;
  const url = chunkUrlFrom(error);

  if (url !== null) {
    try {
      const response = await doFetch(url, { method: 'GET', cache: 'no-store' });
      if (response.status === 404) return 'gone';
      // 429 and 5xx are the throttle and the blip. Both are worth a retry and
      // neither justifies a reload.
      return 'transient';
    } catch {
      // Offline, or a CORS-opaque failure. Not evidence the chunk is gone.
      return 'transient';
    }
  }

  const loaded = deps.loadedBuildId;
  if (loaded === undefined) return 'transient';

  try {
    const response = await doFetch('/api/health', { cache: 'no-store' });
    if (!response.ok) return 'transient';
    const payload = (await response.json()) as { data?: { buildId?: string } };
    const serverBuildId = payload.data?.buildId;
    return serverBuildId !== undefined && serverBuildId !== loaded ? 'gone' : 'transient';
  } catch {
    return 'transient';
  }
}

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export interface LoadOptions {
  fetchImpl?: typeof fetch;
  loadedBuildId?: string;
  reload?: () => void;
  maxAttempts?: number;
  baseDelayMs?: number;
}

/**
 * Import a lazy route, retrying transient failures with backoff and hard-
 * reloading only when the chunk is genuinely gone.
 */
export async function loadChunk<T>(
  factory: () => Promise<T>,
  options: LoadOptions = {},
): Promise<T> {
  const maxAttempts = options.maxAttempts ?? MAX_ATTEMPTS;
  const baseDelay = options.baseDelayMs ?? BASE_DELAY_MS;

  let lastError: unknown;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      return await factory();
    } catch (error) {
      lastError = error;

      const failure = await classifyChunkFailure(error, {
        ...(options.fetchImpl ? { fetchImpl: options.fetchImpl } : {}),
        ...(options.loadedBuildId !== undefined ? { loadedBuildId: options.loadedBuildId } : {}),
      });

      if (failure === 'gone') {
        (options.reload ?? (() => window.location.reload()))();
        // The reload replaces the document; nothing after this matters, but a
        // never-resolving promise keeps React from flashing an error boundary
        // in the moment before it happens.
        return new Promise<T>(() => undefined);
      }

      if (attempt < maxAttempts - 1) {
        await delay(baseDelay * 2 ** attempt);
      }
    }
  }

  throw lastError;
}
